<template>
  <section
    v-if="productSets?.length"
    :aria-label="$t.shopTheLookHeading"
        data-scroll-el="shopTheLook"
  >
    <div v-if="productSets.length === 1" class="relative grid gap-1 lg:cols-4 lg:rows-1">
      <div class="relative full lg:col-span-2" :class="{ 'order-last': primaryImagePosition === 'right' }">
        <vf-video
          v-if="productSets[0].overridePrimaryAsset?.type === 'CMVideo'"
          :autoplay="!lazy"
          class="full"
          fit="cover"
          loop
          :src="productSets[0].overridePrimaryAsset.url"
        />
        <base-picture
          v-else-if="productSets[0].overridePrimaryAsset?.type === 'CMPicture'"
          :alt="productSets[0].overridePrimaryAsset?.alt"
          class="h-a w-full"
          fit="cover"
          :height="productSets[0].overridePrimaryAsset?.height"
          :props-img="{ class: 'h-a!' }"
          :src="productSets[0].overridePrimaryAsset?.url"
          :width="productSets[0].overridePrimaryAsset?.width"
        />
        <base-picture
          v-else-if="productSets[0]?.images?.length"
          :alt="productSets[0].description"
          class="h-a w-full"
          fit="cover"
          :height="gallery.presets.src.height"
          :props-img="{ class: 'h-a!' }"
          :src="getImageTransformations(productSets[0].images[0], gallery.presets.src)"
          :width="gallery.presets.src.width"
        />
        <vf-video
          v-else
          :autoplay="!lazy"
          class="full"
          fit="cover"
          loop
          :src="productSets[0]?.video!.src"
        />
        <base-button
          aria-haspopup="dialog"
          :aria-label="$t.shopTheLookTileCta"
          class="absolute-0 z-0 b-0 bg-transparent p-0 focus-visible:outline-auto"
          @click="$emit('openShopTheLook', productSets[0])"
        />
        <div :class="brandClasses.primaryCta">
          <cms-shared-button
            v-bind="{ ...resolvedLabelTarget, text: labelText || formatProductSetTitle(productSets[0].text) || $t.shopTheLookTileCta }"
            :size="sharedButtonSize"
            @click="$emit('openShopTheLook', productSets[0])"
          />
        </div>
        <cms-shared-hot-zones
          v-if="hasHotZones && getMediaForIndex(0)"
          :media="getMediaForIndex(0)!"
        />
      </div>
      <div class="flex gap-1 overflow-x-auto lg:grid lg:col-span-2 lg:cols-2 lg:rows-2 lg:h-full">
        <div
          v-for="{ id, thumbnail, name } in productSets[0].products?.slice(0, productSetImageThreshold)"
          :key="id"
          v-style:w="{
            sm: (productSets[0].products?.slice(0, productSetImageThreshold)?.length ?? 0) > 2 ? '42%' : '50%',
            lg: '100%',
          }"
          class="relative h-full <lg:shrink-0"
        >
          <base-button :aria-label="$t.viewDetails" class="full" @click="PanelQuickshop.open({ productId: id })">
            <vf-icon class="absolute right-0 top-0 m-2" :name="quickshopIcon" size="md" />
            <base-picture
              :alt="name"
              class="full"
              fit="cover"
              :height="gallery.presets.src.height"
              :src="getImageTransformations(thumbnail, gallery.presets.src)"
              :width="gallery.presets.src.width"
            />
          </base-button>
        </div>
      </div>
    </div>
    <vf-carousel-elevate
      v-else
      class-controls="<lg:hidden h-10 w-10 bg-brand-1 bg-op-50 p-2 c-white shadow-sm @hactive:bg-op-90"
      :show-arrows="false"
      size-controls="md"
      :style="productSets.length === 2 && !isFullWidth && { maxWidth: '90rem', margin: 'auto' }"
    >
      <div
        v-for="(productSet, index) in productSets"
        :key="productSet.productSetId"
        v-style:w="{
          sm: '85%',
          lg: productSets.length >= 3 ? '40%' : `${100 / productSets.length}%`,
        }"
        class="relative"
      >
        <div class="relative full">
          <vf-video
            v-if="productSet.overridePrimaryAsset?.type === 'CMVideo'"
            :autoplay="!lazy"
            class="full"
            fit="cover"
            loop
            :src="productSet.overridePrimaryAsset.url"
          />
          <base-picture
            v-else-if="productSet.overridePrimaryAsset?.type === 'CMPicture'"
            :alt="productSet.overridePrimaryAsset?.alt"
            class="h-a w-full"
            fit="cover"
            :height="productSet.overridePrimaryAsset?.height"
            :props-img="{ class: 'h-a!' }"
            :src="productSet.overridePrimaryAsset?.url"
            :width="productSet.overridePrimaryAsset?.width"
          />
          <base-picture
            v-else-if="productSet?.images?.length"
            :alt="productSet.description"
            class="h-a w-full"
            fit="cover"
            :height="gallery.presets.src.height"
            :props-img="{ class: 'h-a!' }"
            :src="getImageTransformations(productSet.images[0], gallery.presets.src)"
            :width="gallery.presets.src.width"
          />
          <vf-video
            v-else
            :autoplay="!lazy"
            class="full"
            fit="cover"
            loop
            :src="productSet?.video!.src"
          />
          <base-button
            aria-haspopup="dialog"
            :aria-label="$t.shopTheLookTileCta"
            class="absolute-0 z-0 b-0 bg-transparent p-0 focus-visible:outline-auto"
            @click="$emit('openShopTheLook', productSet)"
          />
        </div>
        <div :class="brandClasses.primaryCta">
          <cms-shared-button
            v-bind="{ ...resolvedLabelTarget, text: labelText || formatProductSetTitle(productSet.text) || $t.shopTheLookTileCta }"
            :size="sharedButtonSize"
            @click="$emit('openShopTheLook', productSet)"
          />
        </div>
        <cms-shared-hot-zones
          v-if="hasHotZones && getMediaForIndex(index)"
          :media="getMediaForIndex(index)!"
        />
      </div>
    </vf-carousel-elevate>
    <panel-quickshop class-content="" :gtm-config="{ appSource: 'shop-the-look' }" />
  </section>
</template>

<script lang="ts" setup>
import type { Dialogs } from '#build/dialogs'
import type { SectionProvider } from '#content/contexts/section'
import type { ProductSetWithOverridePrimaryAsset } from '#types/components/cms/shop-the-look'
import type { ResponsiveMedia } from '#types/content'
import type { CMSPicture } from '#types/media'
import type { Target } from '#types/target'
import { SectionContextKey } from '#content/contexts/section'

const props = defineProps<{
  productSets?: ProductSetWithOverridePrimaryAsset[]
  primaryImagePosition?: 'left' | 'right'
  labelTarget?: Target
  labelText?: string
  isFullWidth?: boolean
}>()
defineEmits<{ openShopTheLook: [ProductSetWithOverridePrimaryAsset] }>()

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const config = useAppConfig()
const {
  brandClasses,
  gallery,
  shopTheLook: {
    productSetImageThreshold = 4,
    quickshopIcon = 'quickshop',
    defaultCtaStyle
  } = {}
} = config.pages?.pdp || {}
const { quickshopType } = config.components.product.shopTheLook
const { size: sharedButtonSize } = config.components.cms.sharedButton

const PanelQuickshop = createPanel(quickshopType as keyof Dialogs)

// Get media for a specific productSet index (for hotzones)
const getMediaForIndex = (index: number): ResponsiveMedia | undefined => {
  const override = props?.productSets?.[index]?.overridePrimaryAsset
  if (override?.type === 'CMPicture' && override.imageMap?.length) {
    return { sm: override as CMSPicture }
  }
  return undefined
}

const hasHotZones = computed(() => props.productSets?.some((_, i) => !!getMediaForIndex(i)))

const resolvedLabelTarget = {
  ...props.labelTarget,
  style: props.labelTarget?.style ?? defaultCtaStyle,
}
const formatProductSetTitle = (title: string): string => capitalize(title, { isFirstLetter: true })
</script>
